import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { StockDataService } from '../stock-data.service';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { combineLatest, debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-ainews',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './ainews.component.html',
  styleUrls: ['./ainews.component.css']
})
export class AInewsComponent implements OnInit, OnDestroy {
  newsList: any[] = [];
  type: string = '';
  currentPage: number = 1;
  itemsPerPage: number = 30; // Server-side expects this
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute, 
    private stockDataService: StockDataService,
    private meta: Meta,
    private cdRef: ChangeDetectorRef,
    private titleService: Title
  ) {}

  ngOnInit(): void {
    combineLatest([this.route.params, this.route.queryParams])
    .pipe(
      debounceTime(50), // Add a small delay to allow both changes to settle
      distinctUntilChanged(([prevParams, prevQueryParams], [currParams, currQueryParams]) => {
        // Only emit if both type and page are unchanged
        return (
          prevParams['type'] === currParams['type'] &&
          (parseInt(prevQueryParams['page'], 10) || 1) === (parseInt(currQueryParams['page'], 10) || 1)
        );
      }),
      takeUntil(this.destroy$)
    )
    .subscribe(([params, queryParams]) => {
      const newType = params['type'];
      const newPage = parseInt(queryParams['page'], 10) || 1;

      // Fetch news only if type or page has changed
      if (this.type !== newType || this.currentPage !== newPage) {
        console.log(newType, newPage);
        this.type = newType;
        this.currentPage = newPage;
        this.updatePageTitle(); // Update the page title.
        this.updateRelLinks();
        this.fetchNews();
      }
    });
  }

  // Method to update page title based on news type and page number
  updatePageTitle() {
    const title = `Latest ${this.type} News - Page ${this.currentPage}`;
    const description = `Get the latest updates and insights on ${this.type} news. Page ${this.currentPage} of our coverage.`;
  
    // Use Title service for the page title
    this.titleService.setTitle(title);
    
    // Use Meta service for the meta description
    this.meta.updateTag({ name: 'description', content: description });
  }

  fetchNews(): void {
    this.stockDataService
      .getAINews(undefined, this.type, (this.currentPage - 1) * this.itemsPerPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(news => {
        this.newsList = news.map((item: any) => ({
          ...item,
          routerLink: ['/newsDetail', item.publishedDate, encodeURIComponent(item.headline)]
        }));
        this.cdRef.detectChanges();
      });
  }

  updateRelLinks(): void {
    const baseUrl = `https://www.lambdafin.com/news/${this.type}`;
    const prevUrl = `${baseUrl}?page=${this.currentPage - 1}`;
    const nextUrl = `${baseUrl}?page=${this.currentPage + 1}`;

    // Remove existing tags
    this.meta.removeTag("rel='prev'");
    this.meta.removeTag("rel='next'");

    // Add "prev" link if applicable
    if (this.currentPage > 1) {
      this.meta.addTag({ rel: 'prev', href: prevUrl });
    }

    // Add "next" link if applicable
    if (this.newsList.length === this.itemsPerPage) { // Only if there's more news to display
      this.meta.addTag({ rel: 'next', href: nextUrl });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
